/**
 * Header del portal vecino — naranja con escudo + nav + bell + UserMenu.
 *
 * Sprint 11 v2 S11-V2-03. Refactor Sprint 12 S12-12 + Sprint 13 S13-03:
 * combobox multi-rol alterna vista **intra-portal**. Sprint 14 S14-13:
 * el combobox migra al `UserMenu` (dropdown del avatar) que también
 * absorbe los links del Footer (Ayuda / Datos para transferir / Cerrar
 * sesión). Doctrina: docs/dev/doctrina-portal-vs-superadmin.md §3.1.
 *
 * S21: la nav de secciones es siempre el menú compacto (`NavCompacto`) y los
 * dropdowns del header se renderizan en portal (ver `useMenuAnclado`), porque
 * el shell vive en un contenedor de ancho fijo con overflow-hidden.
 */
import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Bell } from '../ui/Bell';
import { UserMenu } from './UserMenu';
import { useMenuAnclado } from '../ui/useMenuAnclado';
import {
  ROLES_ADMIN,
  ROLES_PUBLICAN_CONTENIDO,
  ROLES_REVISAN_SOLICITUDES,
  ROLES_VEN_REPROGRAMACION,
  ROLES_VEN_SUSPENSIONES,
  tieneAlgunRol,
} from '../../lib/auth/roles';
import type { AuthMeResponse } from '../../lib/api/types';
import { vistaActualPorPath, type VistaActiva } from '../../lib/layout/vista';

interface NavItem {
  to: string;
  label: string;
  visible: boolean;
}

interface HeaderProps {
  me: AuthMeResponse;
  /** Override del pathname para previews dev-only (S14-10 ShellPreview).
   *  Default: usa `useLocation().pathname` del router externo. */
  pathnameOverride?: string;
  /** Callback para previews: si está, el cambio de vista vía UserMenu
   *  llama a este callback en lugar de navegar con react-router. Sprint
   *  14 S14-13. */
  onCambiarVista?: (vista: VistaActiva) => void;
}

const STORAGE_VISTA_PREFERIDA = 'calbucura.vista_preferida';

/** Item de nav activo según el pathname (match más específico gana). */
function navItemActivo(
  items: NavItem[],
  pathname: string,
): NavItem | undefined {
  const matches = items.filter((it) =>
    it.to === '/vecino'
      ? pathname === '/vecino'
      : pathname === it.to || pathname.startsWith(it.to + '/'),
  );
  return matches.sort((a, b) => b.to.length - a.to.length)[0];
}

/**
 * Nav compacta: botón con la sección actual + chevron que despliega la lista
 * completa. Es la nav de secciones del portal en desktop (≥md); en mobile
 * (<md) navega el BottomNav, por eso `hidden md:*`. Se usa SIEMPRE (no hay
 * nav inline) porque el portal vive en un contenedor de ancho fijo
 * (`max-w-portal`=1024px) donde 6-8 ítems inline no caben sin recortar el
 * nombre del usuario.
 *
 * El dropdown se renderiza en un PORTAL a `document.body` con posición fija
 * anclada al botón: así escapa del `overflow-hidden` del contenedor del
 * portal, que de otro modo lo recortaría cuando la página es corta. Se cierra
 * con click-fuera / ESC / scroll / resize.
 */
function NavCompacto({
  items,
  pathname,
  className,
}: {
  items: NavItem[];
  pathname: string;
  className: string;
}) {
  const activo = navItemActivo(items, pathname);
  const { abierto, coords, botonRef, menuRef, alternar, cerrar } =
    useMenuAnclado(pathname);

  return (
    <div className={className}>
      <button
        ref={botonRef}
        type="button"
        onClick={alternar}
        aria-expanded={abierto}
        aria-haspopup="menu"
        aria-label="Secciones"
        className="flex items-center gap-1.5 text-base text-white/90 hover:text-white transition-colors duration-base focus:outline-none focus:ring-2 focus:ring-white/40 rounded-sm px-2 py-1"
      >
        <span className="whitespace-nowrap">{activo?.label ?? 'Menú'}</span>
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
          className="opacity-80"
        >
          <path d="M6 9 L12 15 L18 9" />
        </svg>
      </button>

      {abierto &&
        coords &&
        createPortal(
          <div
            ref={menuRef}
            role="menu"
            aria-label="Secciones"
            style={{ position: 'fixed', top: coords.top, right: coords.right }}
            className="w-56 bg-bg-card border-[0.5px] border-white/15 rounded-md shadow-2xl z-50 overflow-hidden py-1"
          >
            {items.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === '/vecino'}
                role="menuitem"
                onClick={cerrar}
                className={({ isActive }) =>
                  `block px-4 py-2.5 text-sm transition-colors duration-base hover:bg-bg-elevated ${
                    isActive ? 'text-orange font-medium' : 'text-text-primary'
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </div>,
          document.body,
        )}
    </div>
  );
}

export function Header({ me, pathnameOverride, onCambiarVista }: HeaderProps) {
  const location = useLocation();
  const pathname = pathnameOverride ?? location.pathname;
  const vistaActual = vistaActualPorPath(pathname);
  const enVistaAdmin = vistaActual === 'comision';

  // Persistir preferencia: la próxima vez que el usuario entre, abre
  // en la última vista que usaba.
  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_VISTA_PREFERIDA, vistaActual);
    } catch {
      // localStorage puede no estar disponible (modo privado). Ignorar.
    }
  }, [vistaActual]);

  // Nav del vecino — etiquetas según mock (Sprint 11 v2 S11-V2-03).
  const navVecino: NavItem[] = [
    { to: '/vecino', label: 'Inicio', visible: true },
    { to: '/vecino/estado-cuenta', label: 'Mi cuenta', visible: true },
    { to: '/vecino/mis-datos', label: 'Mis datos', visible: true },
    { to: '/vecino/mi-historial', label: 'Historial', visible: true },
    { to: '/vecino/documentos', label: 'Documentos', visible: true },
    { to: '/vecino/avisos', label: 'Avisos', visible: true },
  ];

  const navAdmin: NavItem[] = [
    {
      to: '/admin/solicitudes-acceso',
      label: 'Solicitudes',
      visible: tieneAlgunRol(me.roles_vigentes, ROLES_REVISAN_SOLICITUDES),
    },
    {
      to: '/admin/validaciones',
      label: 'Validaciones',
      visible: tieneAlgunRol(me.roles_vigentes, ROLES_REVISAN_SOLICITUDES),
    },
    {
      to: '/admin/documentos',
      label: 'Documentos',
      visible: tieneAlgunRol(me.roles_vigentes, ROLES_PUBLICAN_CONTENIDO),
    },
    {
      to: '/admin/avisos',
      label: 'Avisos',
      visible: tieneAlgunRol(me.roles_vigentes, ROLES_PUBLICAN_CONTENIDO),
    },
    {
      to: '/admin/tenencias',
      label: 'Tenencias',
      visible: tieneAlgunRol(me.roles_vigentes, ROLES_ADMIN),
    },
    {
      to: '/admin/consulta-vecinos',
      label: 'Vecinos',
      visible: tieneAlgunRol(me.roles_vigentes, ROLES_ADMIN),
    },
    {
      to: '/admin/suspensiones',
      label: 'Suspensiones',
      visible: tieneAlgunRol(me.roles_vigentes, ROLES_VEN_SUSPENSIONES),
    },
    {
      to: '/admin/reprogramacion',
      label: 'Reprogramación',
      visible: tieneAlgunRol(me.roles_vigentes, ROLES_VEN_REPROGRAMACION),
    },
    {
      to: '/admin/reportes',
      label: 'Reportes',
      visible: tieneAlgunRol(me.roles_vigentes, ROLES_ADMIN),
    },
  ];

  const navItems = (enVistaAdmin ? navAdmin : navVecino).filter(
    (item) => item.visible,
  );

  return (
    <header className="bg-orange flex items-center justify-between gap-4 px-4 md:px-8 py-4">
      <Link
        to={enVistaAdmin ? '/admin/solicitudes-acceso' : '/vecino'}
        className="flex items-center gap-3 shrink-0 hover:opacity-90 transition-opacity duration-base"
        aria-label="Ir al inicio"
      >
        <img
          src="/escudo-calbucura-negro.png"
          alt="Escudo Calbucura"
          className="h-9 w-auto block"
        />
        <div className="flex items-baseline gap-2">
          <span className="text-xl font-medium text-white tracking-widest">
            CALBUCURA
          </span>
          <span className="text-sm text-white/70 ml-2 hidden sm:inline">
            / Portal {enVistaAdmin ? 'Comisión' : 'vecinos'}
          </span>
        </div>
      </Link>

      <div className="flex items-center gap-3 lg:gap-5 text-base text-white/85 shrink-0">
        {/* El portal vive en un contenedor de ancho fijo (max-w-portal =
            1024px) con overflow-hidden, así que las 6-8 secciones nunca caben
            inline sin recortar el nombre del usuario. Por eso la nav es
            SIEMPRE el menú compacto (sección actual + dropdown). En mobile
            (<md) navega el BottomNav; por eso `hidden md:block`. */}
        <NavCompacto
          items={navItems}
          pathname={pathname}
          className="hidden md:block"
        />

        <Bell />

        <UserMenu
          me={me}
          pathnameOverride={pathnameOverride}
          onCambiarVista={onCambiarVista}
        />
      </div>
    </header>
  );
}
